/**
 * @fileoverview Not Found Banner Component - Checkbox for marking data as unavailable.
 *
 * Used when a section's data (police report, medical providers, coverage) is not
 * available. Checking this marks the section as 'not_found' in the audit record,
 * which counts toward completion (vs. null = not reviewed).
 */
import React from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface NotFoundCheckboxProps {
  /** Unique ID for checkbox accessibility */
  id: string;
  /** Whether the "not found" state is active */
  checked: boolean;
  /** Callback when checkbox state changes */
  onCheckedChange: (checked: boolean) => void;
  /** Label text shown next to checkbox */
  label: string;
  /** Disable the checkbox */
  disabled?: boolean;
}

/**
 * Checkbox for marking section data as not available.
 *
 * Used at step-level (Police, Treatment) or section-level (Coverage 1P/3P/Health).
 * When checked, the form fields are typically hidden and the section is marked
 * as 'not_found' on save.
 */
export const IngestionNotFoundBanner: React.FC<NotFoundCheckboxProps> = ({
  id,
  checked,
  onCheckedChange,
  label,
  disabled = false,
}) => {
  return (
    <div className="flex items-center gap-1.5">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        disabled={disabled}
        className="data-[state=checked]:bg-zinc-800 data-[state=checked]:border-zinc-800 data-[state=checked]:text-white"
      />
      <Label
        htmlFor={id}
        className="text-xs font-medium text-zinc-600 cursor-pointer"
      >
        {label}
      </Label>
    </div>
  );
};
